import React, { useEffect, useState } from "react";
import {
  FAMILY_REASON_MAX_LENGTH,
  FAMILY_REASON_MIN_LENGTH,
  familyAdminErrorMessage,
  normalizeReason,
  reasonErrorMessage,
  validateReason,
} from "../../utils/familyAdminErrors";

export interface FamilyReasonModalProps {
  isOpen: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  pendingLabel: string;
  isPending: boolean;
  // The failed mutation's error, shown as familyAdminErrorMessage text.
  error?: unknown;
  // Extra red notice under the description (e.g. dissolve consequences).
  warning?: string;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

/**
 * Reason prompt for every Family Account support action. Follows the
 * Payouts reject modal: red header, textarea, Cancel + red confirm.
 * The reason is normalised (whitespace collapsed) and must be 3–200
 * characters without '@'; confirm stays disabled while it is invalid or
 * while the request runs.
 */
const FamilyReasonModal: React.FC<FamilyReasonModalProps> = ({
  isOpen,
  title,
  description,
  confirmLabel,
  pendingLabel,
  isPending,
  error,
  warning,
  onConfirm,
  onClose,
}) => {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setReason("");
      setTouched(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isPending) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, isPending, onClose]);

  if (!isOpen) return null;

  const validation = validateReason(reason);
  const length = normalizeReason(reason).length;
  const showValidation = touched && validation !== null;
  const requestError =
    error !== undefined && error !== null
      ? familyAdminErrorMessage(error)
      : null;

  const handleClose = () => {
    if (!isPending) onClose();
  };

  const handleConfirm = () => {
    setTouched(true);
    if (validation !== null || isPending) return;
    onConfirm(normalizeReason(reason));
  };

  return (
    <div
      className="fixed inset-0 z-[60] overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="family-reason-modal-title"
      data-testid="family-reason-modal"
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity"
          onClick={handleClose}
          data-testid="family-reason-modal-backdrop"
        >
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-red-600 px-6 py-4">
            <h3
              id="family-reason-modal-title"
              className="text-xl font-semibold text-white"
            >
              {title}
            </h3>
            <p className="text-sm text-red-100">{description}</p>
          </div>

          <div className="bg-white dark:bg-gray-800 px-6 py-6 space-y-3">
            {warning && (
              <p className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md px-3 py-2">
                {warning}
              </p>
            )}
            <div>
              <label
                htmlFor="family-reason-input"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Reason *
              </label>
              <textarea
                id="family-reason-input"
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setTouched(true);
                }}
                onBlur={() => setTouched(true)}
                rows={4}
                maxLength={FAMILY_REASON_MAX_LENGTH * 2}
                disabled={isPending}
                aria-invalid={showValidation}
                aria-describedby="family-reason-help"
                className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60"
                placeholder="Enter the reason for this action (no e-mail addresses)..."
              />
              <div
                id="family-reason-help"
                className="mt-1 flex items-start justify-between gap-3 text-xs"
              >
                <span
                  className={
                    showValidation
                      ? "text-red-600 dark:text-red-400"
                      : "text-gray-500 dark:text-gray-400"
                  }
                  data-testid="family-reason-hint"
                >
                  {showValidation && validation
                    ? reasonErrorMessage(validation)
                    : `${FAMILY_REASON_MIN_LENGTH}–${FAMILY_REASON_MAX_LENGTH} characters. Saved in the family audit log with your username.`}
                </span>
                <span
                  className={`flex-shrink-0 tabular-nums ${
                    length > FAMILY_REASON_MAX_LENGTH
                      ? "text-red-600 dark:text-red-400"
                      : "text-gray-500 dark:text-gray-400"
                  }`}
                  data-testid="family-reason-counter"
                >
                  {length}/{FAMILY_REASON_MAX_LENGTH}
                </span>
              </div>
            </div>
            {requestError && (
              <p
                role="alert"
                className="text-sm text-red-600 dark:text-red-400"
                data-testid="family-reason-error"
              >
                {requestError}
              </p>
            )}
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={validation !== null || isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending ? pendingLabel : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FamilyReasonModal;
